import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { usePlatformViewStore } from '@/store/platformViewStore';
import { LoadingScreen } from '@/components/ui/LoadingScreen';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, member, mfaRequired, platformAdmin } = useAuthStore();
  const { viewingOrgId } = usePlatformViewStore();
  const location = useLocation();

  // Show a loading state while auth is resolving (initial session restore).
  if (isLoading) {
    return <LoadingScreen />;
  }

  // MFA verified pending: send to the verification step, not the login form.
  if (mfaRequired) {
    return <Navigate to="/mfa-verify" state={{ from: location }} replace />;
  }

  // Not authenticated at all: send to login, preserving the intended
  // destination so the requester lands where they meant to go (FR-001).
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // A platform admin "visiting" an organisation (platformViewStore) is let
  // through with member still null — every org-scoped read is filtered by
  // viewingOrgId instead (getViewingOrgId(), services/supabase/*.ts), and
  // no write RLS policy exists for platform admins on any org-scoped
  // table, so this never grants real member-level access.
  if (platformAdmin && viewingOrgId) {
    return <>{children}</>;
  }

  // Authenticated via Supabase Auth but with no active org_members row for
  // any organisation: access-denied, never show organisation-scoped data
  // (FR-002).
  if (!member) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
